/**
 * Frontend-specific Variant Hooks
 *
 * These hooks provide access to Frontend-specific CVA variants.
 * They read from FrontendThemeContext (populated by Frontend UiProvider).
 *
 * All return types are non-optional - no runtime checks needed.
 * If used outside Frontend UiProvider, useFrontendTheme() will throw.
 *
 * Shared UI components (Button, Input, etc.) are themed by the wrappers in ../components/ui.
 */

import { useFrontendTheme } from '../state'

import type { CvaFunction } from '@/modules/Infrastructure'

// =============================================================================
// VARIANT INTERFACES (owned by Frontend/Theme)
// =============================================================================

export interface ProductViewSkeletonVariants {
  skeletonSectionVariants: CvaFunction
  skeletonSliderGridVariants: CvaFunction
  skeletonSliderItemVariants: CvaFunction
  skeletonDetailsRowVariants: CvaFunction
  skeletonContentAreaVariants: CvaFunction
}

export interface StarsVariants {
  starsContainerVariants: CvaFunction
  starIconVariants: CvaFunction
}

export interface PriceInfoVariants {
  priceInfoWrapperVariants: CvaFunction
  priceInfoStrikethroughVariants: CvaFunction
  priceInfoFinalPriceVariants: CvaFunction
  priceInfoVatVariants: CvaFunction
  priceInfoMsrpVariants: CvaFunction
  priceInfoTooltipVariants: CvaFunction
}

export interface BannerSliderVariants {
  bannerSectionVariants: CvaFunction
  bannerItemVariants: CvaFunction
  bannerLinkVariants: CvaFunction
  bannerImageVariants: CvaFunction
  bannerFallbackVariants: CvaFunction
  bannerFallbackContentVariants: CvaFunction
  bannerFallbackTitleVariants: CvaFunction
  bannerFallbackCtaVariants: CvaFunction
  bannerNavButtonVariants: CvaFunction
  bannerNavIconVariants: CvaFunction
  bannerSwiperVariants: CvaFunction
  bannerSkeletonVariants: CvaFunction
  swiperPaginationBullet: string
  swiperPaginationBulletActive: string
}

export interface ProductSliderVariants {
  productSliderSectionVariants: CvaFunction
  productSliderTitleVariants: CvaFunction
  productSliderWrapperVariants: CvaFunction
  productSliderSwiperVariants: CvaFunction
  productSliderSlideVariants: CvaFunction
  productSliderSkeletonGridVariants: CvaFunction
  productSliderGridVariants: CvaFunction
  productSliderGridItemVariants: CvaFunction
}

export interface PromoBlockVariants {
  promoBlockSectionVariants: CvaFunction
  promoBlockContainerVariants: CvaFunction
  promoBlockImageSectionVariants: CvaFunction
  promoBlockImageLinkVariants: CvaFunction
  promoBlockImageVariants: CvaFunction
  promoBlockContentSectionVariants: CvaFunction
  promoBlockContentInnerVariants: CvaFunction
  promoBlockTitleVariants: CvaFunction
  promoBlockDescriptionVariants: CvaFunction
  promoBlockSkeletonContentVariants: CvaFunction
  promoBlockSkeletonInnerVariants: CvaFunction
}

export interface CategoryCardsVariants {
  categoryCardsGridVariants: CvaFunction
  categoryCardItemVariants: CvaFunction
  categoryCardImageVariants: CvaFunction
  categoryCardTitleVariants: CvaFunction
  categoryCardSkeletonVariants: CvaFunction
}

export interface HomepageVariants {
  homepageSectionVariants: CvaFunction
}

export interface ReviewsCarouselVariants {
  reviewsCarouselSectionVariants: CvaFunction
  reviewsCarouselSummaryVariants: CvaFunction
  reviewsCarouselAverageVariants: CvaFunction
  reviewsCarouselSwiperVariants: CvaFunction
  reviewsCarouselSlideVariants: CvaFunction
  reviewCardVariants: CvaFunction
  reviewCardNicknameVariants: CvaFunction
  reviewCardSummaryVariants: CvaFunction
  reviewCardBodyVariants: CvaFunction
  reviewCardDateVariants: CvaFunction
  reviewCardProductImageVariants: CvaFunction
  reviewCardProductNameVariants: CvaFunction
  reviewsCarouselSkeletonGridVariants: CvaFunction
  reviewsCarouselSkeletonItemVariants: CvaFunction
}

// =============================================================================
// HOOKS
// =============================================================================

export function useProductViewSkeletonVariants(): ProductViewSkeletonVariants {
  return useFrontendTheme()
}

export function useStarsVariants(): StarsVariants {
  return useFrontendTheme()
}

export function usePriceInfoVariants(): PriceInfoVariants {
  return useFrontendTheme()
}

export function useBannerSliderVariants(): BannerSliderVariants {
  return useFrontendTheme()
}

export function useProductSliderVariants(): ProductSliderVariants {
  return useFrontendTheme()
}

export function usePromoBlockVariants(): PromoBlockVariants {
  return useFrontendTheme()
}

export function useCategoryCardsVariants(): CategoryCardsVariants {
  return useFrontendTheme()
}

export function useHomepageVariants(): HomepageVariants {
  return useFrontendTheme()
}

export function useReviewsCarouselVariants(): ReviewsCarouselVariants {
  return useFrontendTheme()
}
